import React, { useState, useEffect } from 'react';
import { HiX, HiShieldCheck } from 'react-icons/hi';
import { 
  hasConsentBeenGiven, 
  acceptAllConsent, 
  rejectAllConsent, 
  setConsentPreferences,
  getConsentPreferences 
} from '../utils/consentManager';
import { BiCookie } from 'react-icons/bi';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,  // Default to checked for better consent rates
    advertising: true // Default to checked for better consent rates
  });

  useEffect(() => {
    // Check if consent has already been given
    if (!hasConsentBeenGiven()) {
      // Show banner when idle or after initial load
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => setIsVisible(true));
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        const timer = setTimeout(() => setIsVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } else {
      // Load existing preferences
      const existingPreferences = getConsentPreferences();
      if (existingPreferences) {
        setPreferences(existingPreferences);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllConsent();
    setIsVisible(false);
    // Show confirmation
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'consent_banner_action', {
        'event_category': 'consent',
        'event_label': 'accept_all'
      });
    }
  };

  const handleAcceptSelected = () => {
    setConsentPreferences(preferences);
    setIsVisible(false);
    // Show confirmation
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'consent_banner_action', {
        'event_category': 'consent',
        'event_label': 'accept_selected'
      });
    }
  };

  const handleRejectAll = () => {
    rejectAllConsent();
    setIsVisible(false);
    // Show confirmation
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'consent_banner_action', {
        'event_category': 'consent',
        'event_label': 'reject_all'
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto" />
      
      {/* Cookie Banner */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BiCookie className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
              <p className="text-sm text-gray-500">Manage your privacy settings</p>
            </div>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            We use cookies to enhance your browsing experience, provide personalized content, 
            and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
          </p>

          {/* Cookie Categories */}
          {showDetails && (
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <HiShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">Necessary Cookies</h4>
                    <span className="text-sm text-green-600 font-medium">Always Active</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies are essential for the website to function properly and cannot be disabled.
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mt-0.5">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                </div>
              </div>

              {/* Advertising Cookies */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mt-0.5">
                  <input
                    type="checkbox"
                    checked={preferences.advertising}
                    onChange={(e) => setPreferences(prev => ({ ...prev, advertising: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Advertising Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Used to deliver personalized advertisements and measure the effectiveness of advertising campaigns.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       transition-colors font-medium"
            >
              Accept All
            </button>
            
            {showDetails && (
              <button
                onClick={handleAcceptSelected}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 
                         transition-colors font-medium"
              >
                Save Preferences
              </button>
            )}
            
            <button
              onClick={handleRejectAll}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 
                       transition-colors font-medium"
            >
              Reject All
            </button>
          </div>

          {/* Toggle Details */}
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              {showDetails ? 'Hide Details' : 'Customize Settings'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            For more information, please read our{' '}
            <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/cookie-policy" className="text-blue-600 hover:text-blue-700 underline">
              Cookie Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent; 